import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Zap,
  Users,
  Cloud,
  BarChart3,
  Lock,
  ChevronRight
} from "lucide-react";

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900 selection:bg-[#E8F0FE] selection:text-[#4285F4]">

      {/* Background Decor */}
      <div className="absolute inset-0 z-0 h-full w-full bg-white bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none" />

      {/* ================= NAVBAR ================= */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/70 border-b border-slate-200/60">
        <div className="flex justify-between items-center px-6 md:px-12 py-4 max-w-7xl mx-auto">

          {/* Logo */}
          <div className="flex items-center px-4 py-3">
            <img
              src="/logo.png"
              alt="Avaami Pulse Logo"
              className="w-10 h-10 md:w-16 md:h-16
 object-contain -mr-2"
            />

            <h1 className="text-2xl font-bold text-slate-900 tracking-tighter flex items-center">
              Avaami
              <span className="text-[#4285F4] ml-1">Pulse</span>
            </h1>
          </div>

          {/* Navigation */}
          <nav className="flex md:flex items-center gap-4 md:gap-8">


            <button
              onClick={() => navigate("/admin-login")}
              className="text-sm font-medium text-slate-600 hover:text-[#4285F4] transition"
            >
              Admin Portal
            </button>

            <button
              onClick={() => navigate("/employee-login")}
              className="text-sm font-medium text-slate-600 hover:text-[#4285F4] transition"
            >
              Employee Access
            </button>

            <button
              onClick={() => navigate("/signup")}
              className="px-5 py-2.5 text-sm font-semibold bg-slate-900 text-white rounded-full shadow-sm hover:bg-slate-800 transition-all active:scale-95"
            >
              Get Started
            </button>

          </nav>
        </div>
      </header>

      {/* ================= HERO ================= */}
      <section className="relative flex flex-1 items-center justify-center px-4 sm:px-6 py-6 md:py-12 overflow-hidden">


        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl relative z-10"
        >

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E8F0FE] border border-[#E8F0FE] text-[#4285F4] text-xs font-bold uppercase tracking-wider mb-8 mx-auto">

            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4285F4] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4285F4]"></span>
            </span>

            Trusted by Growing Organizations

          </div>

          <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl
 font-black leading-[1.1] text-slate-900 mb-8 tracking-tight">
            Powering the Future of <br />

            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4285F4] to-blue-500">
              Digital Workplaces
            </span>

          </h2>

          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-12 leading-relaxed">
            Avaami Pulse delivers a unified platform for organizational management,
            secure collaboration, performance visibility, and seamless operational control —
            all in one intelligent ecosystem.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4">

            <button
              onClick={() => navigate("/signup")}
              className="group px-6 py-3 md:px-8 md:py-4
 bg-[#4285F4] text-white font-bold rounded-2xl shadow-xl shadow-blue-200 hover:bg-[#3b78e0] transition-all flex items-center justify-center gap-2"
            >
              Start Your Organization

              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>

            <button
              onClick={() => navigate("/admin-login")}
              className="px-8 py-4 border border-slate-200 text-slate-700 font-bold rounded-2xl hover:bg-slate-50 hover:border-slate-300 transition-all"
            >
              Access Platform
            </button>

          </div>

        </motion.div>
      </section>

      {/* ================= STATS ================= */}
      <section className="bg-slate-50/50 py-12 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6">

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">

            <StatItem label="Platform Availability" value="99.9%" />
            <StatItem label="Data Protection" value="Enterprise Grade" />
            <StatItem label="Global Reach" value="Worldwide" />
            <StatItem label="Infrastructure" value="Secure Cloud" />

          </div>

        </div>
      </section>

      {/* ================= FEATURES ================= */}
      <section className="py-16 md:py-32
 px-6 bg-white">

        <div className="max-w-7xl mx-auto">

          <div className="text-center mb-20">

            <h3 className="text-3xl font-bold text-slate-900 mb-4">
              Built for Performance. Designed for Growth.
            </h3>

            <p className="text-slate-500 max-w-2xl mx-auto">
              Everything your organization needs to operate efficiently,
              securely, and at scale.
            </p>

          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8
">

            <FeatureCard
              icon={<ShieldCheck className="w-6 h-6 text-[#4285F4]" />}
              title="Enterprise Security"
              description="Advanced protection standards safeguard your organization’s data and operations."
            />

            <FeatureCard
              icon={<Zap className="w-6 h-6 text-[#4285F4]" />}
              title="High Performance"
              description="Optimized infrastructure ensures fast, reliable, and uninterrupted workflows."
            />

            <FeatureCard
              icon={<Users className="w-6 h-6 text-[#4285F4]" />}
              title="Team Management"
              description="Organize departments, manage members, and streamline internal collaboration."
            />

            <FeatureCard
              icon={<Cloud className="w-6 h-6 text-[#4285F4]" />}
              title="Scalable Infrastructure"
              description="Built to support organizations of all sizes."
            />

            <FeatureCard
              icon={<BarChart3 className="w-6 h-6 text-[#4285F4]" />}
              title="Insightful Analytics"
              description="Gain real-time visibility into performance metrics."
            />

            <FeatureCard
              icon={<Lock className="w-6 h-6 text-[#4285F4]" />}
              title="Secure Access Control"
              description="Ensure the right individuals access the right resources."
            />

          </div>

        </div>

      </section>

      {/* ================= CTA ================= */}
      <section className="px-6 py-20">

        <div className="max-w-5xl mx-auto bg-[#4285F4] rounded-[3rem] p-8 sm:p-10 md:p-20
 text-center relative overflow-hidden shadow-2xl shadow-blue-200">

          <div className="relative z-10">

            <h3 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Ready to Elevate Your Organization?
            </h3>

            <p className="mb-10 text-blue-100 text-lg max-w-xl mx-auto">
              Join forward-thinking teams that rely on Avaami Pulse to manage,
              secure, and scale their operations.
            </p>

            <button
              onClick={() => navigate("/signup")}
              className="px-10 py-4 bg-white text-[#4285F4] font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all"
            >
              Begin Your Journey
            </button>

          </div>

        </div>

      </section>

      {/* ================= FOOTER ================= */}
      <footer className="bg-white text-slate-500 py-16 border-t border-slate-100">

        <div className="max-w-7xl mx-auto px-6 text-center">

          <div className="flex justify-center items-center gap-3 mb-6">
            <img src="/logo.png" className="w-6 h-6" alt="logo" />

            <span className="font-bold text-slate-900 tracking-tight">
              Avaami <span className="text-[#4285F4]">Pulse</span>
            </span>
          </div>

          <p className="text-sm">
            © {new Date().getFullYear()} Avaami Technologies. All rights reserved.
          </p>

        </div>

      </footer>

    </div>
  );
}

/* ================= HELPERS ================= */

function StatItem({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-2xl font-bold text-slate-900">{value}</div>

      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mt-1">
        {label}
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="group p-8 rounded-3xl bg-white border border-slate-100 hover:border-[#E8F0FE] hover:shadow-2xl hover:shadow-blue-50 transition-all duration-300"
    >

      <div className="w-12 h-12 bg-[#E8F0FE] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
        {icon}
      </div>

      <h4 className="text-xl font-bold mb-3 text-slate-900">
        {title}
      </h4>

      <p className="text-slate-500 text-sm leading-relaxed">
        {description}
      </p>

    </motion.div>
  );
}